import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHead from "@/components/base/SectionHead";
import WorkCard from "@/components/base/WorkCard";
import { allSpecials, bannerOf, getSpecial, getWork } from "@/lib/content";

export default function Special() {
  const { id } = useParams();
  const special = getSpecial(id);

  useEffect(() => {
    if (special) {
      document.title = `${special.title} 专题 - 亚洲无码`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) {
        meta.setAttribute(
          "content",
          `${special.title}专题收录 ${special.workIds.length} 部亚洲无码高清成人影视作品，${special.intro.slice(0, 50)}`,
        );
      }
    }
  }, [special]);

  if (!special) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1440px] flex-col items-center px-4 py-24 text-center md:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-primary-500">
            <i className="ri-gallery-line text-2xl" />
          </span>
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-50">专题不存在或已下线</h1>
          <p className="mt-2 max-w-md text-[13px] text-foreground-500">
            找不到对应的专题合集，可能链接已更新。可以返回首页浏览其他精选专题。
          </p>
          <Link
            to="/"
            className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-[13px] font-semibold text-background-950"
          >
            回到首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const works = special.workIds
    .map((workId) => getWork(workId))
    .filter((item): item is NonNullable<ReturnType<typeof getWork>> => Boolean(item));

  const others = allSpecials.filter((item) => item.id !== special.id);

  return (
    <SiteLayout>
      <section className="relative w-full overflow-hidden">
        <div className="relative h-[360px] w-full md:h-[440px]">
          <img
            src={bannerOf(special.banner)}
            alt={`${special.title}专题主图`}
            title={`${special.title} · 亚洲无码精选专题`}
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/80 to-background-950/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-950/40" />
        </div>

        <div className="relative mx-auto -mt-40 w-full max-w-[1440px] px-4 pb-2 md:-mt-52 md:px-8">
          <nav className="flex flex-wrap items-center gap-1.5 text-[11px] text-foreground-400">
            <Link to="/" className="cursor-pointer whitespace-nowrap transition hover:text-primary-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-600" />
            <Link to="/category/all" className="cursor-pointer whitespace-nowrap transition hover:text-primary-300">
              精选专题
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-600" />
            <span className="truncate text-foreground-300">{special.title}</span>
          </nav>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="rounded bg-primary-500 px-2 py-1 font-heading text-[10px] font-bold text-background-950">
              精选专题
            </span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-foreground-400">{special.subtitle}</span>
          </div>

          <h1 className="mt-3 max-w-3xl font-heading text-3xl font-bold leading-tight text-foreground-50 md:text-[40px]">
            {special.title}
          </h1>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-user-star-line text-primary-500" />
              {special.curator}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-check-line text-primary-500" />
              更新于 {special.updatedAt}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-stack-line text-primary-500" />
              收录 {special.workIds.length} 部作品
            </span>
          </div>

          <p className="mt-4 max-w-3xl text-[13px] leading-[1.9] text-foreground-400">{special.intro}</p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1440px] px-4 py-10 md:px-8 md:py-12">
        <SectionHead
          eyebrow="Collection"
          title="专题收录作品"
          desc="以下作品均可在详情页查看完整资料与分集目录，专题内顺序按编辑推荐排列。"
          actionLabel="查看全部作品"
          actionTo="/category/all"
        />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-5">
          {works.map((work, index) => (
            <WorkCard key={work.id} work={work} rank={index + 1} />
          ))}
        </div>
      </section>

      <section className="border-t border-background-300/50 bg-background-100">
        <div className="mx-auto w-full max-w-[1440px] px-4 py-10 md:px-8 md:py-12">
          <SectionHead eyebrow="More Collections" title="相关专题推荐" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {others.map((item) => (
              <Link
                key={item.id}
                to={`/special/${item.id}`}
                className="group flex cursor-pointer items-center gap-4 rounded-lg border border-background-300/60 bg-background-50 p-3 transition hover:border-primary-700/60"
              >
                <div className="relative h-24 w-20 flex-shrink-0 overflow-hidden rounded md:h-28 md:w-24">
                  <img
                    src={bannerOf(item.banner)}
                    alt={`${item.title}专题缩略图`}
                    title={item.title}
                    className="h-full w-full object-cover object-center transition duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-foreground-600">{item.subtitle}</span>
                  <h3 className="mt-1 truncate font-heading text-[15px] font-semibold text-foreground-100 group-hover:text-primary-300">
                    {item.title}
                  </h3>
                  <p className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-foreground-500">{item.intro}</p>
                  <p className="mt-2 text-[10px] text-foreground-600">
                    {item.workIds.length} 部作品 · {item.updatedAt}
                  </p>
                </div>
                <i className="ri-arrow-right-up-line flex-shrink-0 text-foreground-600 transition group-hover:text-primary-400" />
              </Link>
            ))}

            <Link
              to="/category/all"
              className="group flex cursor-pointer items-center justify-between gap-4 rounded-lg border border-dashed border-background-300/70 p-4 transition hover:border-primary-700/60"
            >
              <div>
                <span className="text-[10px] uppercase tracking-[0.2em] text-foreground-600">All Works</span>
                <h3 className="mt-1 font-heading text-[15px] font-semibold text-foreground-100 group-hover:text-primary-300">
                  浏览全站 50 部作品资料
                </h3>
                <p className="mt-1 text-[11px] text-foreground-500">按分类、地区、年份与热度自由筛选</p>
              </div>
              <i className="ri-arrow-right-line text-foreground-600 transition group-hover:text-primary-400" />
            </Link>
          </div>
        </div>
      </section>

      <div className="mx-auto flex w-full max-w-[1440px] flex-wrap items-center gap-2 px-4 py-8 text-[12px] text-foreground-600 md:px-8">
        <i className="ri-arrow-left-line" />
        <Link to="/" className="cursor-pointer transition hover:text-primary-300">
          回到首页
        </Link>
        <span className="text-foreground-700">/</span>
        <Link to="/category/all" className="cursor-pointer transition hover:text-primary-300">
          全部专题作品
        </Link>
      </div>
    </SiteLayout>
  );
}